import { useState } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Play } from 'lucide-react';
import FormModal from '../FormModal';
import { useLanguage } from '../../context/LanguageContext';

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.2 } }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } }
};

export const HeroSection = () => {
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const { t } = useLanguage();

  return (
    <>
      <section id="hero" className="min-h-screen hero-bg flex items-center pt-20 relative overflow-hidden" data-testid="hero-section">
        {/* Animated background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.12, 0.05] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-20 -left-32 w-96 h-96 rounded-full bg-[#4A90D9]"
          />
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.04, 0.08, 0.04] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            className="absolute bottom-20 -right-32 w-[500px] h-[500px] rounded-full bg-[#9B59B6]"
          />
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/3 right-1/4 w-2 h-2 rounded-full bg-[#4A90D9]/40"
          />
          <motion.div
            animate={{ y: [0, 15, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute top-1/2 left-1/4 w-1.5 h-1.5 rounded-full bg-[#E67E22]/30"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="text-left"
            >
              <motion.span
                variants={fadeUp}
                className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-6"
              >
                {t('hero_badge')}
              </motion.span>

              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
                {t('hero_title_1')}{' '}
                <span className="shimmer-text">{t('hero_title_2')}</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-lg sm:text-xl text-white/70 leading-relaxed mb-10 max-w-xl">
                {t('hero_subtitle')}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.04, boxShadow: '0 0 30px rgba(74,144,217,0.4)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowProjectModal(true)}
                  className="px-8 py-4 bg-[#4A90D9] text-white rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-[#5BA0E9] transition-colors duration-200 animate-glow"
                  data-testid="hero-cta-project"
                >
                  <Rocket size={20} />
                  {t('hero_cta_1')}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.04, backgroundColor: 'rgba(255,255,255,0.15)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowDemoModal(true)}
                  className="px-8 py-4 bg-white/10 text-white rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-white/20 transition-colors duration-200 border border-white/20"
                  data-testid="hero-cta-demo"
                >
                  <Play size={20} />
                  {t('hero_cta_2')}
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="hidden lg:block"
            >
              <div className="relative animate-float">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute inset-0 bg-[#4A90D9]/20 rounded-3xl blur-3xl"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/9t293eb3_file_000000005ad071f7900227fff3c88d96.png"
                  alt="Fondateur JoSmart"
                  className="relative rounded-3xl object-cover w-full h-[500px] border border-white/10 depth-card"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <FormModal isOpen={showProjectModal} onClose={() => setShowProjectModal(false)} formType="project" title={t('hero_cta_1')} />
      <FormModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} formType="demo" title={t('hero_cta_2')} />
    </>
  );
};

export default HeroSection;
